import { BaseCustomFeatures } from './base';
import { Feature } from 'ol';
import { Circle } from 'ol/geom';
import apiaryStyle, { apiaryAssociationStyle, apiaryEntityStyle, apiaryMineStyle, apiaryRegularStyle, } from '../styles/apiaries/apiaries';
import apiaryBuffers, { apiaryBufferStyle, } from '../styles/apiaries/apiary-buffers';
import { getPointResolution } from 'ol/proj';
import { i18n } from '@vendor/libs/lang/lang';
import ApiaryStatus from '@vendor/js/enums/ApiaryStatus';
import ApiaryDynamicType from '@vendor/js/enums/ApiaryDynamicType';
export class ApiaryFeatures extends BaseCustomFeatures {
    constructor(options = {}) {
        super('apiaries', {
            style: apiaryStyle,
            visibilityLabel: i18n.trans('map.features.apiaries.label'),
            ...options,
        });
        if (typeof options.visibilityElements == 'undefined') {
            this.setOptions({
                visibilityElements: [
                    {
                        name: this.getVisibilityName(),
                        label: this.options.visibilityLabel ?? this.getVisibilityName(),
                        children: [
                            {
                                name: 'apiary-buffers',
                                label: i18n.trans('map.features.apiaries.related.apiary-buffers.label'),
                            },
                        ],
                    },
                ],
            });
        }
        this.on('add', ({ feature }) => {
            const features = [];
            // add buffer
            const circleFeature = new Feature({
                geometry: new Circle(feature.getGeometry().getCoordinates(), window.App.configuration.apiary_buffer_radius ?? 1500),
            });
            circleFeature.setStyle(apiaryBuffers);
            circleFeature.set('type', feature.get('type'));
            circleFeature.set('muted', feature.get('muted'));
            circleFeature.set('radiusInMeters', window.App.configuration.apiary_buffer_radius ?? 1500);
            features.push(circleFeature);
            this._addRelated(features, feature, this.options.relatedName ?? 'apiary-buffers');
            this.updateRelatedFeaturesRadius();
        });
        this.on('setup', () => {
            this.updateRelatedFeaturesRadius();
        });
    }
    updateRelatedFeaturesRadius() {
        for (let related of this.related) {
            if (related.get('radiusInMeters')) {
                this.updateFeatureRadius(related, related.get('radiusInMeters'));
            }
        }
    }
    updateFeatureRadius(feature, radius) {
        let pointResolution = 1;
        const geometry = feature.getGeometry();
        if (!geometry || !(geometry instanceof Circle)) {
            return;
        }
        if (this.customMap) {
            const viewProjection = this.customMap.ol.getView().getProjection();
            pointResolution = getPointResolution(viewProjection, 1, geometry.getCenter());
        }
        radius = radius / pointResolution;
        geometry.setRadius(radius);
    }
    static getOverlayHtml(apiary) {
        return `
                    ${apiary.member && apiary.member.user
            ? `<strong>${apiary.member.user.name}</strong><br />`
            : ''}
                    
                    ${apiary.status == ApiaryStatus.AWAITING.value
            ? `
                        <small>
                          <span class="badge bg-secondary text-dark">
                            ${i18n.trans(ApiaryStatus.AWAITING.getLabel())}
                          </span>
                        </small>
                      `
            : ''}
                    
                    ${apiary.member &&
            apiary.member.user &&
            apiary.member.user.phone_number
            ? `<small><strong>${i18n.trans('models/common.fields.phone_number')}: </strong>${apiary.member.user.phone_number}</small>`
            : ''}
                    
                    ${apiary.code
            ? `<small><strong>${i18n.trans('models/common.fields.code')}</strong>: ${apiary.code}</small>`
            : ''}
                    
                    ${apiary.hives_count
            ? `<small><strong>${i18n.trans('models/apiaries.fields.hives_count')}</strong>: ${apiary.hives_count}</small>`
            : ''}
                `;
    }
    getLegendItems() {
        let items = [];
        if (this.features.find((f) => f.get('highlighted'))) {
            items = [
                ...items,
                {
                    geomType: 'Point',
                    style: apiaryRegularStyle(5),
                    title: i18n.trans('map.interface_elements.legend.apiaries.highlighted'),
                },
            ];
        }
        if (this.features.find((f) => f.get('type') == ApiaryDynamicType.MINE.value)) {
            items = [
                ...items,
                {
                    geomType: 'Point',
                    style: apiaryMineStyle(5),
                    title: i18n.trans('map.interface_elements.legend.apiaries.mine'),
                },
            ];
        }
        if (this.features.find((f) => f.get('type') == ApiaryDynamicType.ASSOCIATION.value)) {
            items = [
                ...items,
                {
                    geomType: 'Point',
                    style: apiaryAssociationStyle(5),
                    title: i18n.trans('map.interface_elements.legend.apiaries.association'),
                },
            ];
        }
        if (this.features.find((f) => f.get('type') == ApiaryDynamicType.ENTITY.value)) {
            items = [
                ...items,
                {
                    geomType: 'Point',
                    style: apiaryEntityStyle(5),
                    title: i18n.trans('map.interface_elements.legend.apiaries.entity'),
                },
            ];
        }
        if (this.features.find((f) => f.get('type') == ApiaryDynamicType.OTHER.value)) {
            items = [
                ...items,
                {
                    geomType: 'Point',
                    style: apiaryAssociationStyle(5),
                    title: i18n.trans('map.interface_elements.legend.apiaries.other'),
                },
            ];
        }
        if (this.features.length) {
            items = [
                ...items,
                {
                    geomType: 'Circle',
                    style: apiaryBufferStyle(ApiaryDynamicType.MINE.value),
                    title: i18n.trans('map.interface_elements.legend.apiaries.buffer'),
                },
            ];
        }
        return items;
    }
}
